/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { useElementBreakpoints } from '../../hooks/useElementBreakpoints';

import {
	ErrorButton,
	ErrorButtonContainer,
	ErrorFloatingContainer,
	ErrorMessage,
} from './error-screen-parts';
import messages from './messages';

type Props = {
	appearance?: EditorAppearance;
	isAIFC?: boolean;
	onCancel: () => void;
	onRetry?: () => void;
	showRetryButton?: boolean;
	text: React.ReactNode;
};

export const ErrorScreen = ({
	onCancel,
	onRetry,
	text,
	showRetryButton = true,
	appearance,
	isAIFC,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints();

	return (
		<ErrorFloatingContainer
			ref={setBreakpointsElement}
			breakpoint={breakpoint}
			appearance={appearance}
			isAIFC={isAIFC}
		>
			<VisuallyHidden>{formatMessage(messages.ariaErrorTitle)}</VisuallyHidden>
			<ErrorMessage breakpoint={breakpoint} isAIFC={isAIFC}>
				{text}
			</ErrorMessage>
			<ErrorButtonContainer isAIFC={isAIFC}>
				<ErrorButton
					autoFocus
					testId="cancel-button"
					onClick={onCancel}
					appearance={onRetry && showRetryButton ? 'subtle' : 'primary'}
					isAIFC={isAIFC}
				>
					{formatMessage(isAIFC ? messages.closeLabel : messages.cancelLabel)}
				</ErrorButton>
				{onRetry && showRetryButton && (
					<ErrorButton testId={'retry'} onClick={onRetry} appearance="primary" isAIFC={isAIFC}>
						{formatMessage(messages.retryLabel)}
					</ErrorButton>
				)}
			</ErrorButtonContainer>
		</ErrorFloatingContainer>
	);
};
